import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { createDestinations } from "../scene/createDestinations";
import { getDeviceProfile, getJourneyState, measureJourneyMarkers } from "../scene/journeyConfig";
import "./JourneyScene.css";

const supportsWebGL = () => {
  if (typeof window === "undefined" || !window.WebGLRenderingContext) return false;
  try {
    const canvas = document.createElement("canvas");
    return Boolean(canvas.getContext("webgl") || canvas.getContext("experimental-webgl"));
  } catch {
    return false;
  }
};

const seededRandom = (seed) => {
  let value = seed;
  return () => {
    value = (value * 16807) % 2147483647;
    return (value - 1) / 2147483646;
  };
};

const JourneyScene = ({ theme = "light" }) => {
  const mountRef = useRef(null);
  const scrollProgressRef = useRef(0);
  const scrollVelocityRef = useRef(0);
  const lastScrollRef = useRef(0);
  const markersRef = useRef([]);
  const [fallback, setFallback] = useState(false);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount || !supportsWebGL()) {
      setFallback(true);
      return undefined;
    }

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const profile = getDeviceProfile(window.innerWidth, navigator.deviceMemory || 8);
    const { compact } = profile;
    const dark = theme === "dark";
    const palette = dark
      ? { fog: 0x081820, terrain: 0x163b45, ridge: 0x285a66, planet: 0x347d83, accent: 0xc18a4c, structure: 0x78958c, stars: 0xe4e7df }
      : { fog: 0xf3f1e9, terrain: 0xb7c9c3, ridge: 0x8eaaa5, planet: 0x6e9997, accent: 0xb56f3f, structure: 0x5d7771, stars: 0x596a68 };

    const scene = new THREE.Scene();
    scene.fog = new THREE.FogExp2(palette.fog, dark ? 0.04 : 0.055);
    const camera = new THREE.PerspectiveCamera(42, 1, 0.1, 80);
    camera.position.set(0, 0.7, 8.25);
    const cameraTarget = new THREE.Vector3(0, -0.12, -3.4);
    const desiredCamera = new THREE.Vector3();
    const desiredTarget = new THREE.Vector3();

    const renderer = new THREE.WebGLRenderer({ antialias: !compact, alpha: true, powerPreference: "high-performance" });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, profile.pixelRatioCap));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.setClearColor(palette.fog, 1);
    mount.appendChild(renderer.domElement);

    scene.add(new THREE.HemisphereLight(dark ? 0xd3e2dd : 0xffffff, palette.terrain, dark ? 1.55 : 1.8));
    const sun = new THREE.DirectionalLight(dark ? 0xe5bd8e : 0xffdfbd, dark ? 1.75 : 1.9);
    sun.position.set(-5, 7, 6);
    scene.add(sun);

    const world = new THREE.Group();
    scene.add(world);

    const planetGeometry = new THREE.IcosahedronGeometry(compact ? 2.05 : 2.55, profile.planetDetail);
    const planet = new THREE.Mesh(
      planetGeometry,
      new THREE.MeshStandardMaterial({ color: palette.planet, roughness: 0.72, metalness: 0.08, flatShading: true, transparent: true, opacity: dark ? 1 : 0.86 })
    );
    planet.position.set(compact ? 3 : 4.15, 3.15, -7.2);
    planet.rotation.set(0.25, -0.4, 0.12);
    world.add(planet);

    const planetFacets = new THREE.LineSegments(
      new THREE.EdgesGeometry(planetGeometry, 24),
      new THREE.LineBasicMaterial({ color: palette.accent, transparent: true, opacity: dark ? 0.3 : 0.2 })
    );
    planet.add(planetFacets);

    const atmosphere = new THREE.Mesh(
      new THREE.SphereGeometry(compact ? 2.18 : 2.72, 24, 18),
      new THREE.MeshBasicMaterial({ color: palette.accent, transparent: true, opacity: dark ? 0.26 : 0.18, side: THREE.BackSide })
    );
    atmosphere.position.copy(planet.position);
    world.add(atmosphere);

    // A distant world establishes scale without competing with the primary planet.
    const distantWorld = new THREE.Mesh(
      new THREE.IcosahedronGeometry(compact ? 0.38 : 0.52, 1),
      new THREE.MeshStandardMaterial({
        color: palette.ridge,
        roughness: 0.86,
        metalness: 0.02,
        flatShading: true,
        transparent: true,
        opacity: dark ? 0.68 : 0.46,
      })
    );
    distantWorld.position.set(compact ? -2.45 : -4.8, 2.45, -12.5);
    world.add(distantWorld);

    const createRidge = ({ y, z, color, opacity, seed, height }) => {
      const random = seededRandom(seed);
      const points = [];
      const segments = profile.ridgeSegments;
      for (let index = 0; index <= segments; index += 1) {
        const x = -13 + (26 * index) / segments;
        const edgeFade = Math.sin((index / segments) * Math.PI);
        points.push(new THREE.Vector2(x, y + (0.15 + random() * height) * edgeFade));
      }
      points.push(new THREE.Vector2(13, -5), new THREE.Vector2(-13, -5));
      const ridge = new THREE.Mesh(
        new THREE.ShapeGeometry(new THREE.Shape(points)),
        new THREE.MeshLambertMaterial({ color, transparent: true, opacity, flatShading: true, side: THREE.DoubleSide })
      );
      ridge.position.z = z;
      return ridge;
    };
    const farRidge = createRidge({ y: -1.75, z: -5.2, color: palette.ridge, opacity: dark ? 0.76 : 0.56, seed: 29, height: 1.25 });
    const nearRidge = createRidge({ y: -2.25, z: -2.8, color: palette.terrain, opacity: dark ? 0.9 : 0.68, seed: 71, height: 0.92 });
    world.add(farRidge, nearRidge);

    const architecture = new THREE.Group();
    const structureMaterial = new THREE.MeshStandardMaterial({ color: palette.structure, roughness: 0.78, metalness: 0.12, flatShading: true, transparent: true, opacity: dark ? 0.82 : 0.62 });
    const monolith = new THREE.Mesh(new THREE.BoxGeometry(0.28, 2.5, 0.34), structureMaterial);
    monolith.position.set(4.35, -0.65, -3.8);
    monolith.rotation.z = -0.035;
    architecture.add(monolith);

    const gateway = new THREE.Group();
    const gateGeometry = new THREE.BoxGeometry(0.16, 1.75, 0.2);
    const leftPost = new THREE.Mesh(gateGeometry, structureMaterial);
    const rightPost = leftPost.clone();
    leftPost.position.x = -0.62;
    rightPost.position.x = 0.62;
    const lintel = new THREE.Mesh(new THREE.BoxGeometry(1.4, 0.16, 0.2), structureMaterial);
    lintel.position.y = 0.8;
    gateway.add(leftPost, rightPost, lintel);
    gateway.position.set(-4.6, -1.15, -5.5);
    gateway.rotation.y = 0.18;
    architecture.add(gateway);

    const antenna = new THREE.Group();
    const mast = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.07, 1.45, 5), structureMaterial);
    const crossbar = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.06, 0.06), structureMaterial);
    crossbar.position.y = 0.42;
    crossbar.rotation.z = -0.16;
    antenna.add(mast, crossbar);
    antenna.position.set(2.35, -1.38, -6.2);
    architecture.add(antenna);
    world.add(architecture);

    const sectionGroups = {};
    const makeMaterial = (color, opacity = 0.9, metalness = 0.14) => {
      const material = new THREE.MeshStandardMaterial({
        color,
        roughness: 0.72,
        metalness,
        flatShading: true,
        transparent: true,
        opacity,
      });
      material.userData.baseOpacity = opacity;
      return material;
    };
    const accentMaterial = makeMaterial(palette.accent, dark ? 1 : 0.86, 0.18);
    accentMaterial.emissive.setHex(palette.accent);
    accentMaterial.emissiveIntensity = dark ? 0.34 : 0.055;
    const techMaterial = makeMaterial(palette.structure, dark ? 0.96 : 0.8, 0.2);
    techMaterial.emissive.setHex(palette.structure);
    techMaterial.emissiveIntensity = dark ? 0.18 : 0.035;
    const terrainMaterial = makeMaterial(palette.ridge, dark ? 0.84 : 0.68, 0.06);

    Object.assign(sectionGroups, createDestinations({
      world,
      compact,
      materials: { accent: accentMaterial, tech: techMaterial, terrain: terrainMaterial },
    }));
    const experienceWorld = sectionGroups.experience;
    const researchWorld = sectionGroups.research;
    const projectsWorld = sectionGroups.projects;
    const skillsWorld = sectionGroups.skills;
    const testimonialsWorld = sectionGroups.testimonials;
    const contactWorld = sectionGroups.contact;
    const observatoryRing = researchWorld.userData.ring;
    const signal = testimonialsWorld.userData.signal;
    const companionMoon = contactWorld.userData.moon;

    Object.values(sectionGroups).forEach((group) => {
      group.userData.baseScale = group.scale.x;
      group.traverse((object) => {
        if (object.material) {
          const materials = Array.isArray(object.material) ? object.material : [object.material];
          materials.forEach((material) => {
            material.userData.baseOpacity ??= material.opacity;
            material.opacity *= 0.08;
          });
        }
      });
      group.scale.setScalar(0.78);
    });

    const random = seededRandom(17);
    const starCount = profile.starCount;
    const starPositions = new Float32Array(starCount * 3);
    for (let index = 0; index < starCount; index += 1) {
      starPositions[index * 3] = (random() - 0.5) * 24;
      starPositions[index * 3 + 1] = -0.2 + random() * 10;
      starPositions[index * 3 + 2] = -7 - random() * 15;
    }
    const starGeometry = new THREE.BufferGeometry();
    starGeometry.setAttribute("position", new THREE.BufferAttribute(starPositions, 3));
    const stars = new THREE.Points(starGeometry, new THREE.PointsMaterial({ color: palette.stars, size: compact ? 0.025 : 0.032, transparent: true, opacity: dark ? 0.42 : 0.22, sizeAttenuation: true }));
    scene.add(stars);

    const resize = () => {
      const { width, height } = mount.getBoundingClientRect();
      renderer.setSize(width, height, false);
      camera.aspect = width / Math.max(height, 1);
      camera.updateProjectionMatrix();
      markersRef.current = measureJourneyMarkers();
    };
    const updateScroll = () => {
      const maxScroll = Math.max(document.documentElement.scrollHeight - window.innerHeight, 1);
      const nextScroll = window.scrollY;
      const delta = nextScroll - lastScrollRef.current;
      scrollProgressRef.current = nextScroll / maxScroll;
      scrollVelocityRef.current = THREE.MathUtils.clamp(delta / Math.max(window.innerHeight * 0.22, 1), -1, 1);
      lastScrollRef.current = nextScroll;
      if (reducedMotion) renderFrame(false);
    };
    const clock = new THREE.Clock();
    let frame = 0;
    const renderFrame = (scheduleNext = true) => {
      const elapsed = clock.getElapsedTime();
      const progress = scrollProgressRef.current;
      const scrollVelocity = scrollVelocityRef.current;
      const journey = getJourneyState(progress, markersRef.current);
      const focusBlend = journey.focus;
      scrollVelocityRef.current *= 0.9;
      planet.rotation.y = -0.4 + elapsed * 0.055 + progress * Math.PI * 0.72;
      planet.rotation.x = 0.25 + Math.sin(elapsed * 0.12) * 0.035;
      planet.rotation.z = 0.12 + Math.sin(elapsed * 0.055) * 0.018 + scrollVelocity * 0.06;
      planet.position.x = (compact ? 3 : 4.15) - progress * (compact ? 1.15 : 2.35);
      planet.position.y = 3.15 - progress * 0.72;
      planet.position.z = -7.2 + progress * 1.35;
      const planetFocus = Math.max(focusBlend.home || 0, (focusBlend.research || 0) * 0.72, (focusBlend.contact || 0) * 0.45);
      const planetScale = 0.9 + planetFocus * 0.16 + Math.abs(scrollVelocity) * 0.06;
      planet.scale.setScalar(planetScale);
      planet.material.opacity += (0.22 + planetFocus * 0.78 - planet.material.opacity) * 0.08;
      planetFacets.material.opacity += (0.05 + planetFocus * (dark ? 0.25 : 0.15) - planetFacets.material.opacity) * 0.08;
      atmosphere.position.copy(planet.position);
      atmosphere.scale.setScalar(planetScale);
      atmosphere.material.opacity += (0.035 + planetFocus * (dark ? 0.22 : 0.14) - atmosphere.material.opacity) * 0.08;
      atmosphere.rotation.y = planet.rotation.y;
      distantWorld.rotation.y = -elapsed * 0.035 - progress * 0.8;
      distantWorld.rotation.z = elapsed * 0.018;
      distantWorld.position.y = 2.45 + Math.sin(elapsed * 0.1) * 0.08 - progress * 0.35;
      distantWorld.material.opacity += ((dark ? 0.68 : 0.46) * (0.32 + (focusBlend.home || 0) * 0.68) - distantWorld.material.opacity) * 0.06;
      desiredCamera.set(...journey.camera);
      desiredTarget.set(...journey.target);
      if (!reducedMotion) {
        desiredCamera.x += Math.sin(elapsed * 0.025) * 0.055;
        desiredCamera.y += Math.cos(elapsed * 0.02) * 0.035;
      }
      camera.position.lerp(desiredCamera, reducedMotion ? 1 : 0.055);
      cameraTarget.lerp(desiredTarget, reducedMotion ? 1 : 0.05);
      camera.fov += (journey.fov + (reducedMotion ? 0 : Math.abs(scrollVelocity) * 2.2) - camera.fov) * (reducedMotion ? 1 : 0.06);
      camera.updateProjectionMatrix();
      camera.lookAt(cameraTarget);
      farRidge.position.x = progress * 0.72;
      farRidge.position.y = progress * 0.18;
      farRidge.scale.setScalar(1 + progress * 0.08);
      nearRidge.position.x = progress * 1.25;
      nearRidge.position.y = progress * 0.34;
      nearRidge.scale.setScalar(1 + progress * 0.2);
      architecture.position.x = -progress * 1.1;
      architecture.position.y = progress * 0.28;
      architecture.position.z = progress * 0.5;
      architecture.rotation.y = Math.sin(elapsed * 0.08) * 0.03 - progress * 0.12 + scrollVelocity * 0.045;
      const architectureFocus = 0.08 + (focusBlend.home || 0) * 0.92;
      structureMaterial.opacity += ((dark ? 0.82 : 0.62) * architectureFocus - structureMaterial.opacity) * 0.08;
      Object.entries(sectionGroups).forEach(([id, group]) => {
        const focus = focusBlend[id] || 0;
        const visibility = 0.035 + focus * 0.965;
        const nextScale = 0.74 + focus * 0.76 + Math.abs(scrollVelocity) * 0.04;
        group.scale.setScalar(THREE.MathUtils.lerp(group.scale.x, nextScale, 0.08));
        group.traverse((object) => {
          if (object.material) {
            const materials = Array.isArray(object.material) ? object.material : [object.material];
            materials.forEach((material) => {
              material.opacity += (material.userData.baseOpacity * visibility - material.opacity) * 0.08;
            });
          }
        });
      });
      experienceWorld.rotation.y = Math.sin(elapsed * 0.12) * 0.025;
      (experienceWorld.userData.halos || []).forEach((halo, index) => {
        halo.rotation.z = elapsed * (0.08 + index * 0.01);
      });
      researchWorld.rotation.y = elapsed * 0.08 + progress * 0.32;
      observatoryRing.rotation.z = -0.32 + elapsed * 0.035;
      projectsWorld.position.z = -3.25 + Math.sin(elapsed * 0.12) * 0.08;
      projectsWorld.rotation.y = -0.18 + Math.sin(elapsed * 0.1) * 0.035 + scrollVelocity * 0.08;
      skillsWorld.rotation.z = Math.sin(elapsed * 0.1) * 0.025;
      signal.rotation.y = Math.sin(elapsed * 0.16) * 0.12;
      testimonialsWorld.userData.secondSignal.rotation.y = -Math.sin(elapsed * 0.13) * 0.1;
      companionMoon.rotation.y = elapsed * 0.055;
      contactWorld.userData.beam.scale.y = 0.92 + (focusBlend.contact || 0) * 0.18;
      stars.position.x = progress * 0.48;
      stars.position.y = -progress * 0.16;
      stars.rotation.y = elapsed * 0.006 + progress * 0.09;
      renderer.render(scene, camera);
      mount.dataset.destination = journey.activeId;
      if (scheduleNext && !document.hidden) frame = window.requestAnimationFrame(() => renderFrame(true));
    };
    const updateVisibility = () => {
      window.cancelAnimationFrame(frame);
      if (document.hidden) return;
      renderFrame(!reducedMotion);
    };
    resize();
    lastScrollRef.current = window.scrollY;
    updateScroll();
    window.addEventListener("resize", resize);
    window.addEventListener("scroll", updateScroll, { passive: true });
    document.addEventListener("visibilitychange", updateVisibility);
    renderFrame(!reducedMotion);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", resize);
      window.removeEventListener("scroll", updateScroll);
      document.removeEventListener("visibilitychange", updateVisibility);
      scene.traverse((object) => {
        object.geometry?.dispose();
        if (object.material) {
          const materials = Array.isArray(object.material) ? object.material : [object.material];
          materials.forEach((material) => material.dispose());
        }
      });
      renderer.dispose();
      renderer.domElement.remove();
    };
  }, [theme]);

  return (
    <div className="journey-scene" aria-hidden="true" data-testid="journey-scene" data-render-mode={fallback ? "fallback" : "webgl"}>
      <div ref={mountRef} className="journey-scene-canvas" />
      {fallback && <div className="journey-scene-fallback" />}
    </div>
  );
};

export default JourneyScene;
